import { useCallback } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobColors } from "../../../hooks/useJobColors";
import { useGetResolverJobDisplayName } from "../../../hooks/useJobDisplayName";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRoleGroupColors } from "../../../hooks/useRoleColors";
import { getJobInitials } from "../../../utils/roles";
import type { NurseRotation, NurseRotationDay } from "./types";

/** What a People day cell prints in place of the old day/night icon. */
export type PeopleCellLabel = {
	/** Rendered inside the pill: the day's job initials, or the short role code in role departments. */
	short: string;
	/** Unabbreviated text for the cell tooltip and the accessible name. Always populated — several
	    jobs on one day are joined, so this can name more than `short` shows. */
	full: string;
	/** Which axis `short`/`full` came from, so the tooltip can label the value it shows. A job
	    department still yields `"role"` on the archived-job fallback — the tooltip must not call a
	    role a job. */
	kind: "job" | "role";
	/** Cell fill — the app-wide categorical color for that job/role, so the grid reads by hue. The `fill`
	    shade: it carries dark ink (the initials, the hours/location lines) in the full-size cell. */
	bg: string;
	/**
	 * The `dot` shade of the same categorical color, for marks that carry no text.
	 */
	accent: string;
};

/** Separator between the jobs of a same-day double in the cell tooltip. */
const JOB_TOOLTIP_SEPARATOR = " · ";

/**
 * Whether the department plans on JOBS rather than roles: the feature flag AND the department having
 * enabled jobs. Deliberately the same gate as `CoverageSummaryPrototype`'s `jobMode`, so the day
 * cells, the legend, and the job/role filter above them can never disagree on which axis is showing.
 */
export const useIsJobAxis = (): boolean => {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobs = useJobs();
	return jobManagementEnabled && jobs.length > 0;
};

/**
 * Resolver for a People day cell's identity — its glyph AND its fill — on whichever axis the
 * department plans:
 *
 * - Job-managed departments → the job the nurse actually works THAT DAY (`day.jobIds`, sourced from
 *   the day's `nurse_in_shift` rows), abbreviated to initials with the full name in the tooltip. A
 *   same-day double across two jobs shows the earlier job and names both on hover.
 * - Role departments → the nurse's short role code. `nurse_in_shift.job_id` is populated there too,
 *   but it is a single default job for every shift, so printing it on every cell says nothing.
 *
 * The fill comes from the app-wide categorical palette (`useJobColors` / `roleGroupColor`), NOT a
 * People-local scale — so a job is the same hue here as everywhere else in the app, and the two
 * already agree with each other (Nurse shares RN's color, Aide shares CNA's).
 *
 * Returned as a stable callback (rather than a per-cell object) so `React.memo`'d rows and cells
 * keep their referential equality and unchanged cells skip re-render.
 */
export const usePeopleCellLabel = (): ((nurse: NurseRotation, day: NurseRotationDay) => PeopleCellLabel) => {
	const roleGroupColor = useRoleGroupColors();
	const isJobAxis = useIsJobAxis();
	const resolveJobName = useGetResolverJobDisplayName();
	const jobColor = useJobColors();
	const { chosenDepartment } = useHospitalData();
	const roleGroups = chosenDepartment.role_groups;

	return useCallback(
		(nurse: NurseRotation, day: NurseRotationDay): PeopleCellLabel => {
			// `roleGroupColor` resolves a bare short code ("RN") through the canonical role palette
			// before it ever considers role groups, so it is the right entry point for `nurse.role`.
			const roleTone = roleGroupColor(nurse.role, roleGroups);
			const roleLabel: PeopleCellLabel = {
				short: nurse.role,
				full: nurse.role,
				kind: "role",
				bg: roleTone.fill,
				accent: roleTone.dot,
			};
			if (!isJobAxis) return roleLabel;
			const namedJobIds = day.jobIds.filter((jobId) => resolveJobName(jobId));
			const names = namedJobIds.map(resolveJobName);
			// DEFAULT: an unresolvable job id means the job was archived out of both the department's
			// and the global catalog. The cell must still say WHO works it, so it falls back to the
			// role code rather than rendering an empty pill.
			if (names.length === 0) return roleLabel;
			const jobTone = jobColor(namedJobIds[0]);
			return {
				short: getJobInitials(names[0]),
				full: names.join(JOB_TOOLTIP_SEPARATOR),
				kind: "job",
				bg: jobTone.fill,
				accent: jobTone.dot,
			};
		},
		[isJobAxis, resolveJobName, jobColor, roleGroups, roleGroupColor],
	);
};
